import axios from 'axios';
import { Notification, NotificationsResponse, UnreadCountResponse } from '../types';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// API client for notifications operations
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export const notificationsService = {
  async getNotifications(page: number = 1, perPage: number = 20, unreadOnly: boolean = false): Promise<NotificationsResponse> {
    try {
      const response = await api.get('/notifications', {
        params: { page, per_page: perPage, unread_only: unreadOnly }
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch notifications');
    }
  },

  async getPendingActions(): Promise<{ notifications: Notification[]; count: number }> {
    try {
      const response = await api.get('/notifications/pending-actions');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch pending actions');
    }
  },

  async markAsRead(notificationId: number): Promise<void> {
    try {
      await api.put(`/notifications/${notificationId}/read`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to mark notification as read');
    }
  },

  async deleteNotification(notificationId: number): Promise<void> {
    try {
      await api.delete(`/notifications/${notificationId}`);
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete notification');
    }
  },

  async deleteAllNotifications(): Promise<{ message: string; deletedCount: number }> {
    try {
      const response = await api.delete('/notifications/delete-all');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to delete all notifications');
    }
  },

  async getUnreadCount(): Promise<UnreadCountResponse> {
    try {
      const response = await api.get('/notifications/unread-count');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to fetch unread count');
    }
  },

  async createNotification(data: {
    message: string;
    type?: string;
    related_id?: number;
    revision_id?: number;
    project_id?: number;
    action_url?: string;
  }): Promise<{ message: string; id: number }> {
    try {
      const response = await api.post('/notifications', data);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to create notification');
    }
  },
};

export default notificationsService;